import type { LatLng } from '../domain/types';

const STORAGE_KEY = 'dcd:location';

/**
 * A typed-in place stays where it was put; a GPS fix is only ever true of the moment it
 * was taken. Knowing which one is on file is what lets a stale fix be refreshed and a
 * deliberate choice be left alone.
 */
export type LocationSource = 'gps' | 'place';

export interface StoredLocation {
  location: LatLng;
  label: string;
  source: LocationSource;
  at: number;
}

/**
 * How long a GPS fix is taken at face value. Matches the `maximumAge` below: past this
 * the browser would go and read the hardware again anyway, so there is nothing to save
 * by holding on to the old one.
 */
export const LOCATION_TTL_MS = 300_000;

export function isStale(entry: { source: LocationSource; at: number }): boolean {
  return entry.source === 'gps' && Date.now() - entry.at >= LOCATION_TTL_MS;
}

export function readStoredLocation(): StoredLocation | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredLocation>;
    if (
      typeof parsed?.location?.lat !== 'number' ||
      typeof parsed?.location?.lng !== 'number'
    ) {
      return null;
    }
    // Entries written before `source` existed only recorded a label, and 'you' was the
    // one the GPS path wrote. An unreadable `at` counts as long ago, which for a GPS
    // fix means it gets taken again - the safe way round.
    const label = typeof parsed.label === 'string' ? parsed.label : 'you';
    const source: LocationSource =
      parsed.source === 'gps' || parsed.source === 'place'
        ? parsed.source
        : label === 'you'
          ? 'gps'
          : 'place';
    return {
      location: parsed.location,
      label,
      source,
      at: typeof parsed.at === 'number' ? parsed.at : 0,
    };
  } catch {
    return null;
  }
}

export function storeLocation(
  location: LatLng,
  label: string,
  source: LocationSource,
): StoredLocation {
  const entry: StoredLocation = { location, label, source, at: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Private mode; keeping it in memory for this session is good enough.
  }
  return entry;
}

export class LocationError extends Error {
  constructor(
    message: string,
    readonly kind: 'denied' | 'unavailable' | 'unsupported',
  ) {
    super(message);
    this.name = 'LocationError';
  }
}

/**
 * There is deliberately no hardcoded fallback coordinate. The 2016 version defaulted
 * to UC Riverside and silently searched there whenever geolocation failed, which it
 * later had to patch around; failing loudly and asking is the honest behaviour.
 */
export function requestGeolocation(): Promise<LatLng> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new LocationError('This browser has no location support', 'unsupported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }),
      (error) =>
        reject(
          new LocationError(
            error.code === error.PERMISSION_DENIED
              ? 'Location permission was denied'
              : 'Could not get your location',
            error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable',
          ),
        ),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300_000 },
    );
  });
}
